import { ConfirmActionButton } from './ConfirmDialog.js';

export interface BlockConfirmButtonProps {
  username: string;
  /** External pending state, for a caller whose mutation is shared with other rows. */
  isLoading?: boolean;
  /**
   * Performs the block. Returning the mutation's promise (`mutateAsync`)
   * keeps the dialog open until the block has actually been recorded, and
   * prevents a second submission while it is in flight.
   */
  onConfirm: () => void | Promise<unknown>;
}

/**
 * The block action, gated behind a confirming dialog (APP_SPEC W6: "Open a
 * user's profile → Block → confirmation warns..."; APP_SPEC §15: "The
 * system should not rely on UI hiding alone" — this dialog is a UX
 * courtesy, not the enforcement mechanism, which is entirely server-side
 * (docs/APP_PLAN.md §9.6)).
 *
 * The description below must state the block-triggered forfeit cascade
 * explicitly (docs/APP_PLAN.md §2.8, §8.6, §12 M6): blocking immediately
 * forfeits every currently-active game shared with that player, with the
 * blocker recorded as the loser regardless of who blocked whom — a
 * destructive, irreversible consequence that must not hide behind a
 * generic "Block?" prompt.
 *
 * Everything about *how* the prompt behaves — focus, escape, destructive
 * styling, single-submission — comes from the shared `ConfirmDialog`: one
 * confirmation implementation, not several. (This used to cite "CLAUDE.md
 * §12.6" for that; CLAUDE.md has no decimal-numbered sub-sections and no
 * §12 at all, so the citation named a section that has never existed.)
 */
export function BlockConfirmButton({
  username,
  isLoading = false,
  onConfirm,
}: BlockConfirmButtonProps) {
  return (
    <ConfirmActionButton
      triggerLabel="Block"
      title="Block user?"
      description={`Are you sure you want to block ${username}? You will no longer be able to interact normally with this user while they are blocked: ${username} cannot send you friend requests or game invitations, and neither of you will appear in the other's search results or profile. If you have any active games with ${username}, blocking will immediately end them — you will forfeit and lose every one of them right now, even if you started the game.`}
      confirmLabel="Block User"
      destructive
      isPending={isLoading}
      onConfirm={onConfirm}
    />
  );
}
